import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

const FileUpload = ({ onUpload, loading }) => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState([]);

  const clearStatus = () => {
    setUploadStatus(null);
    setUploadProgress([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Initialize progress tracking for all files
    const initialProgress = acceptedFiles.map((file, index) => ({
      id: index,
      filename: file.name,
      status: 'pending',
      error: null
    }));
    setUploadProgress(initialProgress);
    setUploadStatus(`Uploading ${acceptedFiles.length} file(s)...`);

    try {
      // Upload all files at once
      const result = await onUpload(acceptedFiles);
      
      // Check if result exists and has the expected structure
      if (!result) {
        throw new Error('No response received from server');
      }
      
      // Handle new multiple file response format
      if (result.results && Array.isArray(result.results)) {
        const updatedProgress = result.results.map((fileResult, index) => ({
          id: index,
          filename: fileResult.filename || `File ${index + 1}`,
          status: fileResult.status || 'unknown',
          error: fileResult.error || null,
          songId: fileResult.song_id || null
        }));
        
        setUploadProgress(updatedProgress);
        setUploadStatus(
          `Upload complete! ${result.successful_uploads || 0} successful, ${result.failed_uploads || 0} failed.`
        );
      } else if (result.filename && result.song_id) {
        // Old single file response format - convert to new format
        const singleFileProgress = [{
          id: 0,
          filename: result.filename,
          status: 'success',
          error: null,
          songId: result.song_id
        }];
        
        setUploadProgress(singleFileProgress);
        setUploadStatus('Upload complete! 1 successful, 0 failed.');
      } else {
        console.warn('Unexpected response format:', result);
        setUploadStatus('Upload may have completed, but response format was unexpected.');
        
        // Try to show something useful anyway
        const fallbackProgress = acceptedFiles.map((file, index) => ({
          id: index,
          filename: file.name,
          status: 'unknown',
          error: null,
          songId: null
        }));
        setUploadProgress(fallbackProgress);
      }
      
    } catch (error) {
      setUploadStatus(`Upload failed: ${error.message}`);
      setUploadProgress(prev => 
        prev.map(item => ({ ...item, status: 'failed', error: error.message }))
      );
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.flac', '.aac', '.mpeg']
    },
    multiple: true,
    disabled: loading
  });

  return (
    <div className="file-upload">
      <h2>Upload Music</h2>
      
      <div 
        {...getRootProps()} 
        className={`dropzone ${isDragActive ? 'active' : ''} ${loading ? 'disabled' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          {isDragActive ? (
            <p>Drop the music files here...</p>
          ) : (
            <div>
              <div className="upload-icon">📁</div>
              <p>Drag & drop music files here, or click to select</p>
              <p className="file-types">Supported formats: MP3, WAV, FLAC, AAC, MPEG</p>
              <p className="multiple-files">✨ Multiple files supported!</p>
            </div>
          )}
        </div>
      </div>

      {uploadStatus && (
        <div className={`upload-status ${uploadStatus.includes('complete') ? 'success' : uploadStatus.includes('failed') ? 'error' : 'info'}`}>
          <div className="status-content">
            <span>{uploadStatus}</span>
            <button 
              onClick={clearStatus} 
              className="clear-status-btn"
              title="Clear status"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {uploadProgress.length > 0 && (
        <div className="upload-progress">
          <h4>Upload Progress:</h4>
          <div className="progress-list">
            {uploadProgress.map((item) => (
              <div key={item.id} className={`progress-item ${item.status}`}>
                <span className="filename">{item.filename}</span>
                <span className="status">
                  {item.status === 'success' && '✅'}
                  {item.status === 'failed' && '❌'}
                  {item.status === 'pending' && '⏳'}
                </span>
                {item.error && (
                  <div className="error-message">{item.error}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="upload-info">
        <h3>Upload Guidelines:</h3>
        <ul>
          <li>Supported formats: MP3, WAV, FLAC, AAC, MPEG</li>
          <li>Maximum file size: 100MB per file (recommended)</li>
          <li>Multiple files can be uploaded at once</li>
          <li>Files will be automatically added to the playlist</li>
          <li>Duplicate filenames will be renamed automatically</li>
        </ul>
      </div>
    </div>
  );
};

export default FileUpload;